export const STORAGE_KEY = 'cookieConsent'

export const DEFAULT_SCRIPT_CONSENT = {
  analytics: true,
  marketing: true,
  advertising: true,
}

// EU-27 + EEA (IS, LI, NO) + UK + US-CA (ISO 3166-2 codes)
export const REGULATED_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'US-CA',
]

/** Maps site cookie prefs to Google Consent Mode v2 params */
export const prefsToConsentParams = (prefs) => ({
  analytics_storage: prefs.analytics ? 'granted' : 'denied',
  ad_storage: prefs.advertising ? 'granted' : 'denied',
  ad_user_data: prefs.advertising ? 'granted' : 'denied',
  ad_personalization: prefs.advertising ? 'granted' : 'denied',
})

/** Synchronous read of confirmed consent from localStorage/cookie */
export const readStoredConsent = () => {
  if (typeof window === 'undefined') return null
  const storedValues = []

  try {
    storedValues.push(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    // Continue with cookie/session fallbacks.
  }
  try {
    const match = document.cookie.match(new RegExp(`${STORAGE_KEY}=([^;]+)`))
    if (match) storedValues.push(decodeURIComponent(match[1]))
  } catch {
    // Continue with the session fallback.
  }
  try {
    storedValues.push(window.sessionStorage?.getItem(STORAGE_KEY))
  } catch {
    // Storage is unavailable.
  }

  for (const stored of storedValues) {
    if (!stored) continue
    try {
      const prefs = JSON.parse(stored)
      if (prefs.confirmed) return prefs
    } catch {
      // Try the next persistence mechanism.
    }
  }
  return null
}

/** Persist an explicit choice before code that may synchronously reload the page. */
export const persistConsent = prefs => {
  if (typeof window === 'undefined') return prefs
  const confirmedPrefs = { ...prefs, confirmed: true }
  const raw = JSON.stringify(confirmedPrefs)
  let localStorageSaved = false

  try {
    window.localStorage.setItem(STORAGE_KEY, raw)
    localStorageSaved = true
  } catch {
    // Continue with cookie/session fallbacks.
  }

  try {
    if (navigator.cookieEnabled) {
      document.cookie = `${STORAGE_KEY}=${encodeURIComponent(raw)};path=/;max-age=${
        365 * 24 * 3600
      };SameSite=Lax`
    }
  } catch {
    // Continue with the session fallback when needed.
  }
  if (!localStorageSaved) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, raw)
    } catch {
      // Consent still applies to this page through the returned value.
    }
  }

  return confirmedPrefs
}

/** Check if country/state is in a regulated region (EU/EEA/UK/CA) */
export const isRegulatedRegion = (countryCode, state) => {
  if (!countryCode) return false
  const cc = countryCode.toUpperCase()
  if (cc === 'US') {
    return state && state.toUpperCase() === 'CA'
  }
  return REGULATED_REGIONS.includes(cc)
}

const GEO_CACHE_KEY = 'geoData'

/** Read cached geo data from sessionStorage */
export const getCachedGeo = () => {
  try {
    const raw = window.sessionStorage.getItem(GEO_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore
  }
  return null
}

/** Cache geo data to sessionStorage */
export const setCachedGeo = (data) => {
  try {
    window.sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(data))
  } catch {
    // ignore
  }
}
